import { Link } from "react-router-dom";
import { Show, SignUp as ClerkSignUp } from "@clerk/react";

import { useAuth } from "../lib/auth.js";
import { AUTH_REDIRECT_URL, clerkAppearance } from "../lib/clerk-appearance.js";
import { AuthCard, AuthProblem, AuthShell, AuthSkeleton, AuthWidgetSlot } from "./AuthShell.js";

/**
 * The sign-up page.
 *
 * This exists so sign-up stays on this origin. Without it Clerk sends the
 * "Sign up" link to its Account Portal — a different domain, a different look,
 * and one more page that can fail to load before an account exists.
 *
 * The Worker only creates a host row from a *verified* email address, so the
 * flow intentionally follows Clerk's verification step before landing on
 * /dashboard. An unverified account that skips it gets a clear 403 rather than
 * claiming an email it does not own.
 */
export function SignUp() {
  const { host, hostError, loading, clerkUnavailable, refresh, signOut } = useAuth();

  if (clerkUnavailable) {
    return (
      <AuthShell>
        <AuthProblem
          eyebrow="Sign-up unavailable"
          title="Could not reach sign-up"
          message="The sign-up service could not be loaded. That is usually the network rather than you — a guest wifi portal, a proxy, or a content blocker that stops scripts."
          onRetry={() => window.location.reload()}
        />
      </AuthShell>
    );
  }

  if (hostError) {
    return (
      <AuthShell>
        <AuthProblem
          eyebrow="Account unavailable"
          title="We could not load your account"
          message={hostError}
          onRetry={() => void refresh()}
        />
      </AuthShell>
    );
  }

  if (host) {
    return (
      <AuthShell>
        <div className="card auth-card" style={{ textAlign: "center" }}>
          <p className="eyebrow">You are signed in</p>
          <h1>Your account is ready</h1>
          <p className="muted">{host.email}</p>
          <Link className="button" to="/dashboard">
            Go to dashboard
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard>
        <p className="eyebrow">Host sign-up</p>
        <h1>Create your account</h1>
        <p className="muted">
          One account runs every event you host. Guests never need one — they scan the QR code
          and upload.
        </p>

        {loading ? (
          <AuthSkeleton label="Loading secure sign-up…" />
        ) : (
          <>
            <Show when="signed-in">
              <p className="muted">
                You are signed in with Clerk, but this account has no host profile yet. Verify
                your email address, then try again. If it still does not work, sign out and back
                in.
              </p>
              <div className="stack" style={{ gap: "0.75rem" }}>
                <button className="button" type="button" onClick={() => void refresh()}>
                  Try again
                </button>
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={() => void signOut()}
                >
                  Sign out
                </button>
              </div>
            </Show>

            <Show when="signed-out">
              <AuthWidgetSlot label="Loading secure sign-up…">
                <ClerkSignUp
                  routing="path"
                  path="/sign-up"
                  signInUrl="/login"
                  fallbackRedirectUrl={AUTH_REDIRECT_URL}
                  appearance={clerkAppearance}
                />
              </AuthWidgetSlot>
              <div style={{ marginTop: "1.25rem", textAlign: "center", fontSize: "0.88rem" }}>
                <span className="muted">Already have a host account? </span>
                <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>
                  Sign in &rarr;
                </Link>
              </div>
            </Show>
          </>
        )}
      </AuthCard>
    </AuthShell>
  );
}
